import { Elements } from "@stripe/react-stripe-js";
import { Route, Routes, useLocation } from "react-router-dom";
import CheckoutForm from "@/CheckoutForm";
import Return from "@/Return";
import type { Appearance, StripeElementsOptions } from "@stripe/stripe-js";
import FailedCheckoutScreen from "@/pages/FailedCheckoutPage";
import InvalidLinkPage from "@/pages/InvalidLinkPage";
import { useState, useLayoutEffect, useEffect, useRef, useMemo } from "react";

import { useConfig } from "@/hooks/useConfig";
import { useCorrectedDimensions } from "@/hooks/useCorrectedDimensions";
import LoadingOverlay from "@/components/LoadingOverlay";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { AnalyticsProvider, useAnalytics } from "@/contexts/AnalyticsContext";
import { ShortUrlError } from "@/components/ShortUrlError";

const isIPhone = /iphone|ipod/i.test(navigator.userAgent);

// Enable debug panel via URL param: ?debug=1
const urlParams = new URLSearchParams(window.location.search);
// const DEBUG_ENABLED = urlParams.get('debug') === '1';
const DEBUG_ENABLED = null;

const App = () => {
  const location = useLocation();
  const {
    currentGame,
    stripePromise,
    sessionData,
    clientSecret,
    isUnauthorized,
    isShortUrlResolving,
    shortUrlError,
  } = useConfig();
  const theme = useStyleTheme(currentGame);
  const appearance: Appearance = { theme: "stripe" };
  const options: StripeElementsOptions = {
    clientSecret: clientSecret || "",
    appearance,
  };

  const { isLying, isLandscape, width, height } = useCorrectedDimensions();
  
  // Detect WebView type from user agent
  const detectWebViewType = (ua: string): string => {
    if (!ua.includes('iPhone') && !ua.includes('iPad')) return 'Not iOS';
    
    const hasSafari = ua.includes('Safari/');
    const hasVersion = ua.includes('Version/');
    const hasCriOS = ua.includes('CriOS'); // Chrome
    const hasFxiOS = ua.includes('FxiOS'); // Firefox
    const hasEdgiOS = ua.includes('EdgiOS'); // Edge
    
    if (hasCriOS) return 'Chrome iOS';
    if (hasFxiOS) return 'Firefox iOS';
    if (hasEdgiOS) return 'Edge iOS';
    if (hasSafari && hasVersion) return 'Safari/SFSafariVC';
    if (!hasSafari && !hasVersion) return 'WKWebView (bare)';
    return 'Unknown iOS WebView';
  };

  // Debug info for diagnosing WebView issues
  const debugInfo = useMemo(() => ({
    isIPhone,
    webViewType: detectWebViewType(navigator.userAgent),
    userAgent: navigator.userAgent,
    isLying,
    isLandscape,
    width,
    height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    // @ts-ignore
    orientation: window.orientation ?? 'N/A',
    screenOrientation: window.screen?.orientation?.angle ?? 'N/A',
    screenW: window.screen?.width,
    screenH: window.screen?.height,
    vpW: window.visualViewport?.width,
    vpH: window.visualViewport?.height,
    devicePixelRatio: window.devicePixelRatio,
    dataBg: document.body?.getAttribute('data-bg') ?? 'NOT SET',
    htmlWidth: document.documentElement?.clientWidth,
    htmlHeight: document.documentElement?.clientHeight,
    bodyWidth: document.body?.clientWidth,
    bodyHeight: document.body?.clientHeight,
    url: window.location.href,
    hasBgParam: new URLSearchParams(window.location.search).has('bg'),
    bgParamValue: new URLSearchParams(window.location.search).get('bg'),
  }), [isLying, isLandscape, width, height]);
  
  const [renderKey, setRenderKey] = useState(0);
  const [isReady, setIsReady] = useState(!isIPhone);
  const wasLyingRef = useRef(false);
  const hasShownRef = useRef(false);

  useLayoutEffect(() => {
    if (!isIPhone) {
      setIsReady(true);
      return;
    }

    if (wasLyingRef.current && !isLying && !hasShownRef.current) {
      setRenderKey(prev => prev + 1);
      setIsReady(true);
      hasShownRef.current = true;
    }
    
    if (isLying) {
      wasLyingRef.current = true;
    }
    
    if (!isLying && !wasLyingRef.current && !hasShownRef.current) {
      const timer = setTimeout(() => {
        if (!hasShownRef.current) {
          setIsReady(true);
          hasShownRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLying, isLandscape, width, height]);

  useEffect(() => {
    if (!isIPhone) return;
    
    const timer = setTimeout(() => {
      if (!hasShownRef.current) {
        setRenderKey(prev => prev + 1);
        setIsReady(true);
        hasShownRef.current = true;
      }
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);

  if (isUnauthorized) {
    return (
      <div className="App">
        <div className="wrapper">
          <FailedCheckoutScreen />
        </div>
      </div>
    );
  }

  return (
    <AnalyticsProvider gameData={currentGame}>
      {/* Debug panel - enable via ?debug=1 in URL */}
      {DEBUG_ENABLED && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(0,0,0,0.95)',
          color: '#0f0',
          padding: '8px',
          fontSize: '9px',
          fontFamily: 'monospace',
          zIndex: 99999,
          maxHeight: '50vh',
          overflow: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontWeight: 'bold' }}>🔍 WebView Debug</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2))
                  .then(() => alert('Copied!'))
                  .catch(() => prompt('Copy:', JSON.stringify(debugInfo)));
              }}
              style={{ background: '#333', color: '#0ff', border: '1px solid #0ff', borderRadius: '4px', padding: '2px 6px', fontSize: '9px' }}
            >
              📋 Copy
            </button>
          </div>
          <div>UA: <span style={{color: '#ff0', wordBreak: 'break-all'}}>{debugInfo.userAgent}</span></div>
          <div>isIPhone: <span style={{color: '#ff0'}}>{String(debugInfo.isIPhone)}</span></div>
          <div>webViewType: <span style={{color: '#f0f'}}>{debugInfo.webViewType}</span></div>
          <div>orientation (legacy): <span style={{color: '#ff0'}}>{debugInfo.orientation}</span></div>
          <div>screen.orientation.angle: <span style={{color: '#ff0'}}>{debugInfo.screenOrientation}</span></div>
          <div>isLandscape: <span style={{color: debugInfo.isLandscape ? '#0f0' : '#f00'}}>{String(debugInfo.isLandscape)}</span></div>
          <div>isLying: <span style={{color: debugInfo.isLying ? '#f00' : '#0f0'}}>{String(debugInfo.isLying)}</span></div>
          <div>corrected W×H: <span style={{color: '#0ff'}}>{debugInfo.width}×{debugInfo.height}</span></div>
          <div>innerWidth×Height: <span style={{color: '#0ff'}}>{debugInfo.innerWidth}×{debugInfo.innerHeight}</span></div>
          <div>screen W×H: <span style={{color: '#0ff'}}>{debugInfo.screenW}×{debugInfo.screenH}</span></div>
          <div>visualViewport W×H: <span style={{color: '#0ff'}}>{debugInfo.vpW}×{debugInfo.vpH}</span></div>
          <div>devicePixelRatio: <span style={{color: '#0ff'}}>{debugInfo.devicePixelRatio}</span></div>
          <div style={{borderTop: '1px solid #333', marginTop: '4px', paddingTop: '4px'}}>
            <div>data-bg: <span style={{color: debugInfo.dataBg === 'NOT SET' ? '#f00' : '#0f0'}}>{debugInfo.dataBg}</span></div>
            <div>bg param: <span style={{color: debugInfo.hasBgParam ? '#0f0' : '#f00'}}>{debugInfo.bgParamValue ?? 'NOT IN URL'}</span></div>
            <div>html clientW×H: <span style={{color: '#0ff'}}>{debugInfo.htmlWidth}×{debugInfo.htmlHeight}</span></div>
            <div>body clientW×H: <span style={{color: '#0ff'}}>{debugInfo.bodyWidth}×{debugInfo.bodyHeight}</span></div>
            <div style={{marginTop: '4px', fontSize: '8px', wordBreak: 'break-all'}}>URL: {debugInfo.url}</div>
          </div>
        </div>
      )}
      
      <div 
        className="App" 
        style={{
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.2s ease-in',
        }}
        key={`app-${renderKey}`}
      >
        <div className="wrapper">
      {/* Show loading during short URL resolution */}
      {isShortUrlResolving ? (
        <LoadingOverlay 
          isVisible={true} 
          message="Loading payment link..." 
          isAbsolute={false}
          theme={theme}
        />
      ) : shortUrlError ? (
        <ShortUrlError 
          error={shortUrlError}
          onRetry={() => window.location.reload()}
        />
      ) : location.pathname === "/return" || location.pathname === "/return/" ? (
        <Routes>
          <Route path="/return" element={<Return />} />
        </Routes>
      ) : clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={options}>
          <Routes>
            <Route
              path="/checkout"
              element={
                <CheckoutForm
                  clientSecret={clientSecret}
                  gameData={currentGame}
                  sessionData={sessionData}
                />
              }
            />
            <Route path="/return" element={<Return />} />
            <Route
              path="/failed-checkout"
              element={<FailedCheckoutScreen />}
            />
            <Route path="/" element={(() => {
              return <InvalidLinkPage />;
            })()} />
          </Routes>
        </Elements>
      ) : location.pathname === "/" || location.pathname === "" ? (
        (() => {
          return <InvalidLinkPage />;
        })()
      ) : (
        <LoadingOverlay 
          isVisible={true} 
          message="Loading payment system..." 
          isAbsolute={false}
          theme={theme}
        />
      )}
      </div>
    </div>
    </AnalyticsProvider>
  );
};

export default App;
